import "./Coordinators.scss";
import React, { useEffect, useState } from "react";
import { Avatar, Card } from "@mui/material";
import { Add, Delete, Edit, Info } from "@mui/icons-material";
import { DataGrid, GridToolbar, GridActionsCellItem } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import DataContainer from "../../components/dataContainer/DataContainer";
import {
    CustomNoRowsOverlay,
    CustomLoadingOverlay,
} from "../../utils/gridOverlays";
import { useIntl } from "react-intl";
import AddModal from "../../components/addModal/AddModal";
import { useConfirm } from "material-ui-confirm";
import { stringAvatar } from "../../utils/avatar";
import DataValidationError from "../../errors/DataValidationError";
import {
    createCoordinator,
    deleteCoordinator,
    getCoordinators,
} from "../../api/coordinator";
import { getOfficers } from "../../api/officerAccount";
import { getClients } from "../../api/client";
import { camelToSnakeUpperCase } from "../../utils/general";
import useAbility from "../../hooks/useAbility";

const Coordinators = () => {
    //#region States
    const { can } = useAbility();
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);
    const [dataRows, setDataRows] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [modalLoading, setModalLoading] = useState(false);
    //#endregion

    //#region other Hooks
    const intl = useIntl();
    const confirm = useConfirm();
    //#endregion

    //#region useEffect
    useEffect(() => {
        setupClients();
        setupUsers();
        getData();
    }, []);

    useEffect(() => {}, [isModalOpen]);
    //#endregion

    //#region Handlers
    const handleCloseAdd = () => {
        setIsModalOpen(false);
    };
    const handleOpenAdd = () => {
        setIsModalOpen(true);
    };
    const getData = async () => {
        setLoading(true);
        try {
            let data = await getCoordinators();
            setDataRows(
                data.map((row) => ({
                    ...row.client,
                    ...row.account,
                    ...row.account.officerInfo,
                    id: row._id,
                }))
            );
        } catch (error) {
            toast.error(intl.formatMessage({ id: error.message }));
        }
        setLoading(false);
    };
    const setupUsers = async () => {
        try {
            let users = await getOfficers();
            setUsers(users);
        } catch (error) {}
    };
    const setupClients = async () => {
        try {
            let clients = await getClients();
            setClients(clients);
        } catch (error) {}
    };
    const addCoordinator = async (data) => {
        try {
            setModalLoading(true);
            await createCoordinator(data);
            toast.success(
                intl.formatMessage({ id: "COORDINATORS:COORDINATOR_ADDED" })
            );
            setIsModalOpen(false);
            await getData();
        } catch (error) {
            if (error instanceof DataValidationError) {
                error.fields.forEach((field) => {
                    if (field.kind === "unique") {
                        toast.error(
                            intl.formatMessage(
                                { id: "ERROR:UNIQUE" },
                                {
                                    field: intl.formatMessage({
                                        id: `COORDINATORS:${camelToSnakeUpperCase(
                                            field.path
                                        )}`,
                                    }),
                                    value: field.value,
                                }
                            )
                        );
                    } else {
                        toast.error(
                            intl.formatMessage(
                                { id: "ERROR:FORMAT" },
                                {
                                    field: intl.formatMessage({
                                        id: `COORDINATORS:${camelToSnakeUpperCase(
                                            field.path
                                        )}`,
                                    }),
                                    value: field.value,
                                }
                            )
                        );
                    }
                });
            } else {
                toast.error(intl.formatMessage({ id: error.message }));
            }
        } finally {
            setModalLoading(false);
        }
    };
    const deleteSelectedCoordinator = async (params) => {
        try {
            await confirm({
                title: intl.formatMessage({ id: "WARNING" }),
                description: intl.formatMessage(
                    { id: "COORDINATORS:COORDINATOR_DELETE_CONFIRM" },
                    { name: params.row.name }
                ),
                confirmationText: intl.formatMessage({ id: "GLOBAL:CONFIRM" }),
                cancellationText: intl.formatMessage({ id: "GLOBAL:CANCEL" }),
            });
        } catch (error) {
            return;
        }
        setLoading(true);
        try {
            await deleteCoordinator(params.row.id);
            toast.success(
                intl.formatMessage({ id: "COORDINATORS:COORDINATOR_DELETED" })
            );
            await getData();
        } catch (error) {
            toast.error(intl.formatMessage({ id: error.message }));
        } finally {
            setLoading(false);
        }
    };
    //#endregion

    //#region constants
    const dataColumns = [
        {
            field: "actions",
            type: "actions",
            minWidth: 50,
            maxWidth: 50,
            getActions: (params) => [
                <GridActionsCellItem
                    icon={<Info />}
                    onClick={() => {}}
                    label={intl.formatMessage({ id: "ACTIONS:DETAILS" })}
                    showInMenu
                />,
                ...(can("UPDATE", "CLIENT_COORDINATORS")
                    ? [
                          <GridActionsCellItem
                              icon={<Edit />}
                              onClick={() => {}}
                              label={intl.formatMessage({ id: "ACTIONS:EDIT" })}
                              showInMenu
                          />,
                      ]
                    : []),
                ...(can("DELETE", "CLIENT_COORDINATORS")
                    ? [
                          <GridActionsCellItem
                              icon={<Delete />}
                              onClick={() => deleteSelectedCoordinator(params)}
                              label={intl.formatMessage({
                                  id: "ACTIONS:DELETE",
                              })}
                              showInMenu
                          />,
                      ]
                    : []),
            ],
        },
        {
            field: "image",
            headerName: intl.formatMessage({ id: "COORDINATORS:IMAGE" }),
            renderCell: (params) =>
                params.value ? (
                    <Avatar
                        src={`data:image;base64,${params.value}`}
                        alt="avatar"
                        sx={{ width: 40, height: 40 }}
                    />
                ) : (
                    <Avatar
                        {...stringAvatar(
                            `${params.row.firstName} ${params.row.lastName}`
                        )}
                    />
                ),
            minWidth: 100,
            maxWidth: 100,
            height: 50,
        },
        {
            field: "username",
            headerName: intl.formatMessage({ id: "COORDINATORS:USERNAME" }),
            flex: 1,
            minWidth: 160,
        },
        {
            field: "firstName",
            headerName: intl.formatMessage({ id: "COORDINATORS:FIRST_NAME" }),
            flex: 1,
            minWidth: 160,
        },
        {
            field: "lastName",
            headerName: intl.formatMessage({ id: "COORDINATORS:LAST_NAME" }),
            flex: 1,
            minWidth: 160,
        },
        {
            field: "phone",
            headerName: intl.formatMessage({ id: "COORDINATORS:PHONE" }),
            flex: 1,
            minWidth: 200,
        },
        {
            field: "email",
            headerName: intl.formatMessage({ id: "COORDINATORS:EMAIL" }),
            flex: 1,
            minWidth: 250,
        },
        {
            field: "name",
            headerName: intl.formatMessage({ id: "COORDINATORS:CLIENT" }),
            flex: 1,
            minWidth: 250,
        },
    ];

    const headerButtons = [
        ...(can("CREATE", "CLIENT_COORDINATORS")
            ? [
                  {
                      name: intl.formatMessage({ id: "COORDINATORS:CREATE" }),
                      props: {
                          onClick: handleOpenAdd,
                          variant: "contained",
                          startIcon: <Add />,
                      },
                  },
              ]
            : []),
    ];
    const modalFields = [
        {
            name: "account",
            label: intl.formatMessage({ id: "COORDINATORS:USER" }),
            type: "select",
            rules: {
                required: intl.formatMessage({
                    id: "FORM:ERROR_MESSAGES:REQUIRED",
                }),
            },
            options: users.map((user) => ({
                id: user._id,
                value: user._id,
                label: user.username,
            })),
            fullWidth: true,
        },
        {
            name: "client",
            label: intl.formatMessage({ id: "COORDINATORS:CLIENT" }),
            type: "select",
            rules: {
                required: intl.formatMessage({
                    id: "FORM:ERROR_MESSAGES:REQUIRED",
                }),
            },
            options: clients.map((client) => ({
                id: client._id,
                value: client._id,
                label: client.name,
            })),
            fullWidth: true,
        },
    ];
    //#endregion

    return (
        <DataContainer
            buttons={headerButtons}
            onRefresh={() => {
                getData();
            }}
        >
            {isModalOpen && (
                <AddModal
                    open={isModalOpen}
                    handleClose={handleCloseAdd}
                    fields={modalFields}
                    title={intl.formatMessage({ id: "COORDINATORS:CREATE" })}
                    submit={addCoordinator}
                    buttons={[
                        {
                            props: {
                                type: "submit",
                                variant: "contained",
                                color: "secondary",
                            },
                            label: intl.formatMessage({ id: "GLOBAL:SUBMIT" }),
                        },
                    ]}
                    buttonsBoxProps={[]}
                    loading={modalLoading}
                />
            )}
            {can("CONSULT", "CLIENT_COORDINATORS") ? (
                <Card sx={{ height: "100%" }}>
                    <DataGrid
                        loading={loading}
                        columns={dataColumns}
                        rows={dataRows}
                        pageSize={pageSize}
                        onPageSizeChange={(newPageSize) =>
                            setPageSize(newPageSize)
                        }
                        rowsPerPageOptions={[10, 20, 30, 50, 100]}
                        components={{
                            Toolbar: GridToolbar,
                            LoadingOverlay: CustomLoadingOverlay,
                            NoRowsOverlay: () =>
                                CustomNoRowsOverlay(
                                    intl.formatMessage({
                                        id: "COORDINATORS:NO_COORDINATORS",
                                    })
                                ),
                            NoResultsOverlay: () =>
                                CustomNoRowsOverlay(
                                    intl.formatMessage({
                                        id: "COORDINATORS:NO_RESULTS",
                                    })
                                ),
                        }}
                        disableSelectionOnClick
                        onCellDoubleClick={() => {}}
                    />
                </Card>
            ) : (
                <>THIS IS A PLACEHOLDER</>
            )}
        </DataContainer>
    );
};

export default Coordinators;
